import { useEffect, useRef } from "react";

import { useJitsu } from "@jitsu/react";

import { JitsuEvent } from "@/data/events/JitsuEvent";

import { ChevronLeftIcon } from "@vericus/cadmus-icons";
import { Button, ControlButton, Text } from "@vericus/cadmus-ui";

import { show } from "@ebay/nice-modal-react";

import { ConnectionBar } from "@/components/connection-bar";
import { LoadingPage } from "@/components/loading-page";
import { AbandonAttemptModal } from "@/features/socratic/components/abandon-attempt-modal";
import {
  AlreadyCompletedScreen,
  RecoveryScreen,
  SocraticDialogue,
} from "@/features/socratic/components/dialogue";
import { hasDialogueResponse } from "@/features/socratic/helpers/has-dialogue-response";
import {
  useBackProtection,
  useReloadProtection,
  useStartDialogue,
} from "@/features/socratic/hooks";
import { SocraticPageError } from "@/features/socratic/socratic-page-error";
import { StartedDialogue } from "@/features/socratic/types";
import {
  AnswerBlockFragment,
  AssessmentFragment,
  SocraticDialogueFragment,
  StartSocraticDialogueError,
  UserFragment,
} from "@/generated/graphql";
import { CadmusLogo } from "@/ui/shared/CadmusLogo";
import { ManualWithMutations } from "@/ui/shared/ManualWithMutations";

import * as pageStyles from "./socratic.css";

interface Props {
  workId: string;
  /** Student owner of the work */
  student: UserFragment;
  /** Assessment the work belongs to */
  assessment: Pick<AssessmentFragment, "id" | "name">;
  answerBlock: AnswerBlockFragment;
  dialogue: SocraticDialogueFragment;
  onReattemptPressed: () => void;
  navigateToWork: () => void;
  numAttemptsRemaining: number;
}

/**
 * SocraticPage is the main layout component.
 *
 * It is responsible for starting the given Socratic Dialogue (or handling start errors),
 * and then passing this started Dialogue down to the SocraticDialogue component.
 */
export function SocraticPage(props: Props) {
  const {
    workId,
    student,
    assessment,
    answerBlock,
    dialogue,
    onReattemptPressed,
    navigateToWork,
    numAttemptsRemaining,
  } = props;

  const manualMountElement = useRef<HTMLDivElement>(null);
  const { track } = useJitsu();

  const { startDialogue, isStartingDialogue, isStartError, startError } =
    useStartDialogue();

  // Unload protection
  const shouldPreventUnload = !isStartError && !hasDialogueResponse(dialogue);

  useReloadProtection(shouldPreventUnload);

  useBackProtection(() => {
    if (shouldPreventUnload) {
      showAbandonAttemptModal();
    } else {
      navigateToWork();
    }
  });

  // Navigation
  const showAbandonAttemptModal = () => {
    show(AbandonAttemptModal, {
      onConfirm: () => {
        track(JitsuEvent.VIDEO_RESPONSE_ATTEMPT_FORFEITED, {
          dialogue_id: dialogue.id,
        });
        navigateToWork();
      },
      remainingAttempts: numAttemptsRemaining,
    });
  };
  const safeNavigateToWork = () => {
    if (shouldPreventUnload) {
      showAbandonAttemptModal();
    } else {
      navigateToWork();
    }
  };

  // Start the dialogue on load
  const hasStartedRef = useRef<boolean>(false);
  useEffect(() => {
    // prevent double-firing in dev (Strict Mode)
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;
    startDialogue({
      dialogueId: dialogue.id,
      workId,
    });
  }, [dialogue.id, workId, startDialogue]);

  const startedDialogue: StartedDialogue | null =
    dialogue.startedAt != null ? (dialogue as StartedDialogue) : null;

  const isLoading = isStartingDialogue || !hasStartedRef.current;
  const isError = isStartError;

  const isAlreadyStartedError =
    Array.isArray(startError) &&
    startError.includes(StartSocraticDialogueError.AlreadyStarted);

  const isAlreadyCompletedError =
    Array.isArray(startError) &&
    startError.includes(StartSocraticDialogueError.AlreadyCompleted);

  const isUnknownError =
    (isError && !isAlreadyStartedError && !isAlreadyCompletedError) ||
    !startedDialogue;

  return (
    <>
      {/* Surfaces "Working Offline" so a student keeps the tab open while a
          recording upload waits to resume on reconnect. */}
      <ConnectionBar />
      {isLoading && <LoadingPage />}
      {!isLoading && isUnknownError && (
        <SocraticPageError navigateToWork={navigateToWork} />
      )}
      {!isLoading && !isUnknownError && (
        <>
          {!isAlreadyStartedError && !isAlreadyCompletedError && (
            <div className={pageStyles.layout}>
              <Header
                assessmentName={assessment.name}
                studentName={student.name}
                manualMountElement={manualMountElement}
              />
              <PageNavigation
                onCancel={safeNavigateToWork}
                onComplete={safeNavigateToWork}
              />
              <SocraticDialogue
                workId={workId}
                startedDialogue={startedDialogue}
                answerBlock={answerBlock}
                onReattemptPressed={onReattemptPressed}
                numAttemptsRemaining={numAttemptsRemaining}
              />
            </div>
          )}
          {isAlreadyStartedError && !isAlreadyCompletedError && (
            <div className={pageStyles.layout}>
              <Header
                assessmentName={assessment.name}
                studentName={student.name}
                manualMountElement={manualMountElement}
              />
              <PageNavigation
                onCancel={navigateToWork}
                onComplete={navigateToWork}
                hideCompleteButton
              />
              <RecoveryScreen
                answerBlock={answerBlock}
                otherDialogue={dialogue}
              />
            </div>
          )}
          {isAlreadyCompletedError && (
            <div className={pageStyles.layout}>
              <Header
                assessmentName={assessment.name}
                studentName={student.name}
                manualMountElement={manualMountElement}
              />
              <PageNavigation
                onCancel={navigateToWork}
                onComplete={navigateToWork}
                hideCompleteButton
              />
              <AlreadyCompletedScreen
                answerBlock={answerBlock}
                completedDialogue={dialogue}
                navigateToWork={navigateToWork}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}

function Header(props: {
  assessmentName: string;
  studentName: string;
  manualMountElement: React.RefObject<HTMLDivElement>;
}) {
  return (
    <div className={pageStyles.header}>
      <CadmusLogo />
      <Text
        kind="bodyLine"
        color="neutralgrey500"
        className={pageStyles.assessmentTitle}
      >
        {props.assessmentName} {"•"} {props.studentName}
      </Text>
      <ManualWithMutations
        mountElement={props.manualMountElement}
        trigger={
          <ControlButton iconName="Book" aria-label="Open Cadmus Manual" />
        }
      />
    </div>
  );
}

function PageNavigation(props: {
  onComplete?: () => void;
  onCancel: () => void;
  hideCompleteButton?: boolean;
}) {
  return (
    <div className={pageStyles.responseHeader}>
      <Button
        kind="outline"
        size="md"
        onClick={props.onCancel}
        icon={<ChevronLeftIcon label="" size={24} />}
      />
      <Text kind="displayThree" as="h1" className={pageStyles.responseTitle}>
        Record video response
      </Text>
      {props.hideCompleteButton !== true && (
        <Button onClick={props.onComplete} kind="secondary">
          Complete
        </Button>
      )}
    </div>
  );
}
